import {
  ActionButton,
  Alert,
  Box,
  Button,
  CircularProgress,
  DataTable,
  Grid,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type {
  CostDocumentKind,
  CostDocumentLocale,
  CostItemView,
  CostPaymentView,
  CostReceiptView,
  CostRecoveryCaseView,
} from '@munifocus/contracts-gov';
import type { Permission } from '@munifocus/access';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { CostItemDialog } from '../components/cost-recovery/CostItemDialog';
import {
  EscalateDialog,
  PaymentDialog,
  PlanDialog,
  ReasonDialog,
  StatementDialog,
} from '../components/cost-recovery/CostDialogs';
import {
  useAddItemReceipt,
  useCancelPlan,
  useCostRecovery,
  useGenerateCostDocument,
  useReviewCostItem,
  useVoidCostItem,
  useVoidPayment,
} from '../lib/cost-recovery-api';
import {
  day,
  hoursLabel,
  ITEM_STATUS_KIND,
  money,
  rate,
  RECOVERY_STATUS_KIND,
} from '../lib/cost-recovery-display';
import { apiErrorStatus } from '../lib/mutation-toast';
import { useFile } from '../lib/files-api';
import { FileLoadError } from '../components/FileLoadError';
import { useSessionAccess } from '../lib/administration-api';
import { useAuth } from '../providers/AuthProvider';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useConfirmAction } from '../lib/useConfirmAction';

function Tile({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant={emphasis ? 'h5' : 'subtitle1'}
        component="p"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function Receipts({ receipts }: { receipts: CostReceiptView[] }) {
  if (!receipts.length) return <>—</>;
  return (
    <Stack spacing={0.25}>
      {receipts.map((r) =>
        r.downloadUrl ? (
          <Link key={r.id} href={r.downloadUrl} target="_blank" rel="noopener noreferrer">
            {r.filename}
          </Link>
        ) : (
          <Typography key={r.id} variant="body2">
            {r.filename}
          </Typography>
        ),
      )}
    </Stack>
  );
}

function Section({ title, help, children }: { title: string; help?: string; children: ReactNode }) {
  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" component="h2">
            {title}
          </Typography>
          {help && (
            <Typography variant="caption" color="text.secondary">
              {help}
            </Typography>
          )}
        </Box>
        {children}
      </Stack>
    </HairlineCard>
  );
}

/**
 * A file's cost recovery: what the remediation cost, what was billed, what the
 * owner has paid, and — when they have not — the escalation.
 *
 * Every control is offered only to someone the API will let use it: the file's
 * own permissions decide recording, approving and payments; escalation is the
 * municipality's, so it is asked of the active municipality.
 */
export function FileCostRecovery() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuth();
  const scope = useOptionalScope();
  // Asked in order, so nothing is requested that can only answer 404: the file
  // first (out of reach → the "no access" state), whose detail says whether its
  // municipality has cost recovery on, and only then the account.
  const file = useFile(id);
  const moduleOff = file.data?.modules?.costRecovery === false;
  const query = useCostRecovery(file.data && !moduleOff ? id : undefined);
  const access = useSessionAccess('file', id, user?.id);
  const held = new Set<Permission>(access.data?.permissions ?? []);
  const can = (p: Permission) => held.has(p);

  const view = query.data;
  const canEscalateHere =
    !!view?.localGovernmentId &&
    scope?.active?.kind === 'local_government' &&
    scope.active.id === view.localGovernmentId &&
    scope.can('cost:escalate');

  const [dialog, setDialog] = useState<
    null | 'item' | 'statement' | 'payment' | 'plan' | 'escalate'
  >(null);

  return (
    <AppShell>
      <Stack spacing={2}>
        <Link
          component={RouterLink}
          to={`/files/${id}`}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start' }}
        >
          <Icons.ArrowLeft size={iconSize.sm} />
          {t('gov.costRecovery.backToFile')}
        </Link>

        {(file.isLoading || query.isLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {file.isError && <FileLoadError error={file.error} />}
        {moduleOff && <Alert severity="info">{t('gov.costRecovery.notEnabled')}</Alert>}
        {query.isError && (
          <Alert severity={apiErrorStatus(query.error) === 404 ? 'info' : 'error'}>
            {apiErrorStatus(query.error) === 404
              ? t('gov.costRecovery.notEnabled')
              : t('gov.costRecovery.loadError')}
          </Alert>
        )}

        {view && (
          <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'space-between', alignItems: { md: 'center' } }}
            >
              <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  {/* The topbar already says "Cost recovery" as the page's
                      h1; this names the file it is the account of. */}
                  <Typography variant="h5" component="h2">
                    {view.reference}
                  </Typography>
                  <StatusChip
                    status={RECOVERY_STATUS_KIND[view.status]}
                    label={t(`gov.costRecovery.status.${view.status}`)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {view.address}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {can('cost:record') && (
                  <ActionButton
                    label={t('gov.costRecovery.actions.addCost')}
                    icon={Icons.Plus}
                    onClick={() => setDialog('item')}
                  />
                )}
                {can('cost:approve') && view.totals.unbilledCents > 0 && !view.escalation && (
                  <ActionButton
                    tone="secondary"
                    label={t('gov.costRecovery.actions.issueStatement')}
                    icon={Icons.FileText}
                    onClick={() => setDialog('statement')}
                  />
                )}
                {can('cost:payment') && view.firstIssuedOn && (
                  <ActionButton
                    tone="secondary"
                    label={t('gov.costRecovery.actions.recordPayment')}
                    icon={Icons.Banknote}
                    onClick={() => setDialog('payment')}
                  />
                )}
                {can('cost:payment') &&
                  view.firstIssuedOn &&
                  !view.plan &&
                  !view.escalation &&
                  view.balance.balanceCents > 0 && (
                    <ActionButton
                      tone="secondary"
                      label={t('gov.costRecovery.actions.plan')}
                      icon={Icons.Calendar}
                      onClick={() => setDialog('plan')}
                    />
                  )}
                {canEscalateHere && view.canEscalate && (
                  <ActionButton
                    tone="danger"
                    label={t('gov.costRecovery.actions.escalate')}
                    icon={Icons.Gavel}
                    onClick={() => setDialog('escalate')}
                  />
                )}
              </Stack>
            </Stack>

            <PageIntro>{t('gov.costRecovery.intro')}</PageIntro>

            <BalanceCard view={view} lang={lang} />
            {view.escalation && <EscalationCard view={view} lang={lang} />}
            <ItemsSection view={view} can={can} />
            {view.plan && <PlanSection view={view} canManage={can('cost:payment')} />}
            {view.firstIssuedOn && <PaymentsSection view={view} canManage={can('cost:payment')} />}
            <DocumentsSection view={view} canGenerate={can('cost:approve')} />

            <CostItemDialog
              fileId={view.fileId}
              open={dialog === 'item'}
              onClose={() => setDialog(null)}
            />
            <StatementDialog
              view={view}
              open={dialog === 'statement'}
              onClose={() => setDialog(null)}
            />
            <PaymentDialog
              view={view}
              open={dialog === 'payment'}
              onClose={() => setDialog(null)}
            />
            <PlanDialog view={view} open={dialog === 'plan'} onClose={() => setDialog(null)} />
            <EscalateDialog
              view={view}
              open={dialog === 'escalate'}
              onClose={() => setDialog(null)}
            />
          </>
        )}
      </Stack>
    </AppShell>
  );
}

function BalanceCard({ view, lang }: { view: CostRecoveryCaseView; lang: string }) {
  const { t } = useTranslation();
  const b = view.balance;
  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 2 }}>
          <Tile label={t('gov.costRecovery.balance.billed')} value={money(b.billedCents, lang)} />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Tile label={t('gov.costRecovery.balance.paid')} value={money(b.paidCents, lang)} />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Tile
            label={t('gov.costRecovery.balance.interest')}
            value={money(b.interestCents, lang)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tile
            label={t('gov.costRecovery.balance.owing')}
            value={money(b.balanceCents, lang)}
            emphasis
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Tile
            label={
              view.daysOverdue > 0
                ? t('gov.costRecovery.balance.overdue', { count: view.daysOverdue })
                : t('gov.costRecovery.balance.due')
            }
            value={day(view.dueOn, lang)}
          />
        </Grid>
      </Grid>
      <Stack spacing={0.5} sx={{ mt: 2 }}>
        {(view.totals.pendingCents > 0 || view.totals.unbilledCents > 0) && (
          <Typography variant="body2" color="text.secondary">
            {t('gov.costRecovery.balance.notYetBilled', {
              pending: money(view.totals.pendingCents, lang),
              unbilled: money(view.totals.unbilledCents, lang),
            })}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {view.terms.rateBps > 0
            ? t(
                view.termsLocked
                  ? 'gov.costRecovery.balance.termsLocked'
                  : 'gov.costRecovery.balance.termsCurrent',
                {
                  rate: rate(view.terms.rateBps, lang),
                  method: t(`gov.costRecovery.interestMethod.${view.terms.method}`),
                  grace: view.terms.graceDays,
                },
              )
            : t('gov.costRecovery.balance.noInterest')}
          {b.perDiemCents > 0 &&
            ` ${t('gov.costRecovery.balance.perDiem', { amount: money(b.perDiemCents, lang) })}`}
        </Typography>
        {b.creditCents > 0 && (
          <Alert severity="info">
            {t('gov.costRecovery.balance.credit', { amount: money(b.creditCents, lang) })}
          </Alert>
        )}
      </Stack>
    </HairlineCard>
  );
}

function EscalationCard({ view, lang }: { view: CostRecoveryCaseView; lang: string }) {
  const { t } = useTranslation();
  const e = view.escalation;
  if (!e) return null;
  return (
    <Alert severity="warning" icon={<Icons.Gavel size={iconSize.md} />}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t(`gov.costRecovery.escalation.${e.kind}`)}
      </Typography>
      <Typography variant="body2">
        {t('gov.costRecovery.escalation.on', {
          date: day(e.escalatedOn, lang),
          name: e.escalatedBy.name ?? t('gov.costRecovery.someone'),
        })}
        {e.kind === 'tax_roll' && ` ${t('gov.costRecovery.escalation.taxRollDocument')}`}
      </Typography>
      {e.note && <Typography variant="body2">{e.note}</Typography>}
    </Alert>
  );
}

function ItemsSection({
  view,
  can,
}: {
  view: CostRecoveryCaseView;
  can: (p: Permission) => boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const actionsColumn = useRowActionsColumn();
  const review = useReviewCostItem(view.fileId);
  const voidItem = useVoidCostItem(view.fileId);
  const addReceipt = useAddItemReceipt(view.fileId);
  const confirm = useConfirmAction();
  const fileInput = useRef<HTMLInputElement>(null);
  const [receiptFor, setReceiptFor] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<CostItemView | null>(null);
  const [voiding, setVoiding] = useState<CostItemView | null>(null);

  const approve = async (item: CostItemView) => {
    const ok = await confirm({
      title: t('gov.costRecovery.item.approveTitle'),
      body: t('gov.costRecovery.item.approveBody', {
        amount: money(item.amountCents, lang),
        description: item.description,
      }),
      confirmLabel: t('gov.costRecovery.item.approve'),
    });
    if (ok) review.mutate({ itemId: item.id, dto: { decision: 'approve' } });
  };

  const showActions = can('cost:record') || can('cost:approve');

  return (
    <Section title={t('gov.costRecovery.items.title')} help={t('gov.costRecovery.items.help')}>
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/heic,image/tiff"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && receiptFor) addReceipt.mutate({ itemId: receiptFor, file });
          e.target.value = '';
        }}
      />
      <DataTable
        rows={view.items}
        getRowKey={(row) => row.id}
        label={t('gov.costRecovery.items.title')}
        minWidth={860}
        initialSortKey="incurredOn"
        emptyLabel={t('gov.costRecovery.items.empty')}
        columns={[
          {
            key: 'incurredOn',
            label: t('gov.costRecovery.items.col.date'),
            nowrap: true,
            sortValue: (row) => row.incurredOn,
            cell: (row) => day(row.incurredOn, lang),
          },
          {
            key: 'description',
            label: t('gov.costRecovery.items.col.description'),
            primary: true,
            sortValue: (row) => row.description,
            cell: (row) => (
              <Stack spacing={0.25}>
                <Typography variant="body2">
                  {t(`gov.costRecovery.category.${row.category}`)} — {row.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    row.vendorName,
                    row.reference,
                    row.category === 'officer_time'
                      ? t('gov.costRecovery.items.timed', {
                          hours: hoursLabel(row.quantityHundredths, lang),
                          rate: money(row.unitAmountCents, lang),
                        })
                      : null,
                    t('gov.costRecovery.items.recordedBy', {
                      name: row.recordedBy.name ?? t('gov.costRecovery.someone'),
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {row.reading?.totalCents != null && row.reading.totalCents !== row.amountCents && (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    {t('gov.costRecovery.reading.differs', {
                      amount: money(row.reading.totalCents, lang),
                    })}
                  </Typography>
                )}
                {row.reviewNote && (
                  <Typography variant="caption" color="text.secondary">
                    {row.reviewNote}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            key: 'amount',
            label: t('gov.costRecovery.items.col.amount'),
            align: 'right',
            nowrap: true,
            sortValue: (row) => row.amountCents,
            cell: (row) => (
              <Typography
                variant="body2"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  textDecoration:
                    row.status === 'void' || row.status === 'rejected' ? 'line-through' : undefined,
                }}
              >
                {money(row.amountCents, lang)}
              </Typography>
            ),
          },
          {
            key: 'status',
            label: t('gov.costRecovery.items.col.status'),
            sortValue: (row) => row.status,
            cell: (row) => (
              <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                <StatusChip
                  status={ITEM_STATUS_KIND[row.status]}
                  label={t(`gov.costRecovery.itemStatus.${row.status}`)}
                />
                {!row.billable && (
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.costRecovery.items.notBilled')}
                  </Typography>
                )}
                {row.billedOn && (
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.costRecovery.items.billedOn', { date: day(row.billedOn, lang) })}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            key: 'receipts',
            label: t('gov.costRecovery.items.col.receipts'),
            optional: false,
            cell: (row) => <Receipts receipts={row.receipts} />,
          },
          ...(showActions
            ? [
                {
                  ...actionsColumn,
                  cell: (row: CostItemView) => (
                    <RowActions>
                      {can('cost:approve') && row.status === 'pending' && (
                        <RowAction
                          label={t('gov.costRecovery.item.approve')}
                          icon={Icons.Check}
                          onClick={() => void approve(row)}
                        />
                      )}
                      {can('cost:approve') && row.status === 'pending' && (
                        <RowAction
                          label={t('gov.costRecovery.item.reject')}
                          icon={Icons.X}
                          tone="danger"
                          onClick={() => setRejecting(row)}
                        />
                      )}
                      {can('cost:record') && !row.billedDocumentId && row.status !== 'void' && (
                        <RowAction
                          label={t('gov.costRecovery.item.attachReceipt')}
                          icon={Icons.Paperclip}
                          onClick={() => {
                            setReceiptFor(row.id);
                            fileInput.current?.click();
                          }}
                        />
                      )}
                      {can('cost:approve') &&
                        !row.billedDocumentId &&
                        (row.status === 'pending' || row.status === 'approved') && (
                          <RowAction
                            label={t('gov.costRecovery.item.void')}
                            icon={Icons.Archive}
                            tone="danger"
                            onClick={() => setVoiding(row)}
                          />
                        )}
                    </RowActions>
                  ),
                },
              ]
            : []),
        ]}
      />
      {view.requireSecondApprover && (
        <Typography variant="caption" color="text.secondary">
          {t('gov.costRecovery.items.secondApprover')}
        </Typography>
      )}

      <ReasonDialog
        open={!!rejecting}
        title={t('gov.costRecovery.item.rejectTitle')}
        body={t('gov.costRecovery.item.rejectBody')}
        confirmLabel={t('gov.costRecovery.item.reject')}
        busy={review.isPending}
        onClose={() => setRejecting(null)}
        onConfirm={(note) =>
          rejecting &&
          review.mutate(
            { itemId: rejecting.id, dto: { decision: 'reject', note } },
            { onSuccess: () => setRejecting(null) },
          )
        }
      />
      <ReasonDialog
        open={!!voiding}
        title={t('gov.costRecovery.item.voidTitle')}
        body={t('gov.costRecovery.item.voidBody')}
        confirmLabel={t('gov.costRecovery.item.void')}
        busy={voidItem.isPending}
        onClose={() => setVoiding(null)}
        onConfirm={(reason) =>
          voiding &&
          voidItem.mutate({ itemId: voiding.id, reason }, { onSuccess: () => setVoiding(null) })
        }
      />
    </Section>
  );
}

function PlanSection({ view, canManage }: { view: CostRecoveryCaseView; canManage: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cancel = useCancelPlan(view.fileId);
  const confirm = useConfirmAction();
  const plan = view.plan;
  if (!plan) return null;
  const nextNumber = plan.standing.next?.number ?? null;

  return (
    <Section
      title={t('gov.costRecovery.plan.title')}
      help={t('gov.costRecovery.plan.summary', {
        total: money(plan.totalCents, lang),
        count: plan.installmentCount,
        agreed: day(plan.agreedOn, lang),
      })}
    >
      <Alert severity={plan.standing.inGoodStanding ? 'success' : 'warning'}>
        {plan.standing.inGoodStanding
          ? t('gov.costRecovery.plan.onTrack', {
              paid: money(plan.standing.paidSinceAgreedCents, lang),
            })
          : t('gov.costRecovery.plan.behind', {
              due: money(plan.standing.dueToDateCents, lang),
              paid: money(plan.standing.paidSinceAgreedCents, lang),
            })}
      </Alert>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={t('gov.costRecovery.plan.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('gov.costRecovery.plan.col.number')}</TableCell>
              <TableCell>{t('gov.costRecovery.plan.col.dueOn')}</TableCell>
              <TableCell align="right">{t('gov.costRecovery.plan.col.amount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.schedule.map((i) => (
              <TableRow key={i.number} selected={i.number === nextNumber}>
                <TableCell>{i.number}</TableCell>
                <TableCell>{day(i.dueOn, lang)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {money(i.amountCents, lang)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {canManage && (
        <Box>
          <Button
            size="small"
            color="error"
            disabled={cancel.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: t('gov.costRecovery.plan.cancelTitle'),
                body: t('gov.costRecovery.plan.cancelBody'),
                confirmLabel: t('gov.costRecovery.plan.cancel'),
                tone: 'danger',
              });
              if (ok) cancel.mutate(undefined);
            }}
          >
            {t('gov.costRecovery.plan.cancel')}
          </Button>
        </Box>
      )}
    </Section>
  );
}

function PaymentsSection({ view, canManage }: { view: CostRecoveryCaseView; canManage: boolean }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const actionsColumn = useRowActionsColumn();
  const voidPayment = useVoidPayment(view.fileId);
  const [voiding, setVoiding] = useState<CostPaymentView | null>(null);

  return (
    <Section title={t('gov.costRecovery.payments.title')}>
      <DataTable
        rows={view.payments}
        getRowKey={(row) => row.id}
        label={t('gov.costRecovery.payments.title')}
        minWidth={720}
        initialSortKey="receivedOn"
        initialSortDir="desc"
        emptyLabel={t('gov.costRecovery.payments.empty')}
        columns={[
          {
            key: 'receivedOn',
            label: t('gov.costRecovery.payments.col.date'),
            nowrap: true,
            primary: true,
            sortValue: (row) => row.receivedOn,
            cell: (row) => day(row.receivedOn, lang),
          },
          {
            key: 'amount',
            label: t('gov.costRecovery.payments.col.amount'),
            align: 'right',
            nowrap: true,
            sortValue: (row) => row.amountCents,
            cell: (row) => (
              <Typography
                variant="body2"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  textDecoration: row.voidedAt ? 'line-through' : undefined,
                }}
              >
                {money(row.amountCents, lang)}
              </Typography>
            ),
          },
          {
            key: 'method',
            label: t('gov.costRecovery.payments.col.method'),
            sortValue: (row) => row.method,
            cell: (row) => (
              <Stack spacing={0.25}>
                <Typography variant="body2">
                  {t(`gov.costRecovery.method.${row.method}`)}
                </Typography>
                {row.reference && (
                  <Typography variant="caption" color="text.secondary">
                    {row.reference}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            key: 'recordedBy',
            label: t('gov.costRecovery.payments.col.recordedBy'),
            sortValue: (row) => row.recordedBy.name ?? '',
            cell: (row) => (
              <Stack spacing={0.25}>
                <Typography variant="body2">{row.recordedBy.name ?? '—'}</Typography>
                {row.voidedAt && (
                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                    {t('gov.costRecovery.payments.voided', { reason: row.voidReason ?? '' })}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            key: 'receipts',
            label: t('gov.costRecovery.items.col.receipts'),
            cell: (row) => <Receipts receipts={row.receipts} />,
          },
          ...(canManage
            ? [
                {
                  ...actionsColumn,
                  cell: (row: CostPaymentView) =>
                    row.voidedAt ? null : (
                      <RowActions>
                        <RowAction
                          label={t('gov.costRecovery.payments.void')}
                          icon={Icons.Archive}
                          tone="danger"
                          onClick={() => setVoiding(row)}
                        />
                      </RowActions>
                    ),
                },
              ]
            : []),
        ]}
      />
      <ReasonDialog
        open={!!voiding}
        title={t('gov.costRecovery.payments.voidTitle')}
        body={t('gov.costRecovery.payments.voidBody')}
        confirmLabel={t('gov.costRecovery.payments.void')}
        busy={voidPayment.isPending}
        onClose={() => setVoiding(null)}
        onConfirm={(reason) =>
          voiding &&
          voidPayment.mutate(
            { paymentId: voiding.id, reason },
            { onSuccess: () => setVoiding(null) },
          )
        }
      />
    </Section>
  );
}

function DocumentsSection({
  view,
  canGenerate,
}: {
  view: CostRecoveryCaseView;
  canGenerate: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const generate = useGenerateCostDocument(view.fileId);
  const kinds: CostDocumentKind[] = view.firstIssuedOn
    ? view.escalation?.kind === 'lien'
      ? ['cost_statement', 'lien_certificate']
      : ['cost_statement']
    : [];

  return (
    <Section
      title={t('gov.costRecovery.documents.title')}
      help={t('gov.costRecovery.documents.help')}
    >
      {view.documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('gov.costRecovery.documents.empty')}
        </Typography>
      ) : (
        <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {view.documents.map((d) => (
            <Stack
              component="li"
              key={d.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ justifyContent: 'space-between' }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(`gov.costRecovery.documents.kind.${d.kind}`)} · v{d.version} ·{' '}
                  {t(`gov.costRecovery.language.${d.locale}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('gov.costRecovery.documents.meta', {
                    asOn: day(d.asOn, lang),
                    balance: money(d.totals.balanceCents, lang),
                    name: d.generatedBy.name ?? t('gov.costRecovery.someone'),
                  })}
                </Typography>
              </Box>
              {d.downloadUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  href={d.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<Icons.Download size={iconSize.sm} />}
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                >
                  {t('gov.costRecovery.documents.download')}
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {canGenerate && kinds.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {kinds.flatMap((kind) =>
            (['en', 'fr'] as CostDocumentLocale[]).map((locale) => (
              <Button
                key={`${kind}-${locale}`}
                size="small"
                disabled={generate.isPending}
                startIcon={<Icons.RefreshCw size={iconSize.sm} />}
                onClick={() => generate.mutate({ kind, locale })}
              >
                {t('gov.costRecovery.documents.reprint', {
                  kind: t(`gov.costRecovery.documents.kind.${kind}`),
                  language: t(`gov.costRecovery.language.${locale}`),
                })}
              </Button>
            )),
          )}
        </Stack>
      )}
    </Section>
  );
}
